/**
 * Guided Redshift setup: generates the copy-paste script that provisions a
 * `qombra_connector` database user in the customer's AWS account (via the
 * Redshift Data API, so no psql is needed), grants read access plus CREATE
 * per schema (Qombra writes only to its own qombra_-prefixed prediction
 * tables — BigQuery-guided parity), opens the security group to Qombra's
 * egress IP on the Redshift port, warns when the endpoint is not publicly
 * accessible, and saves a credentials JSON onto the user's Desktop for
 * upload back into Qombra.
 *
 * Works for provisioned clusters and Serverless workgroups. Every input is
 * validated before interpolation so nothing shell-hostile can land in the
 * copied script. The password is generated locally on the user's machine
 * and never passes through Qombra's servers until they upload the file.
 */

// AWS region ids: eu-central-1, us-east-1, us-gov-west-1, ...
const REGION_RE = /^[a-z]{2}(-[a-z]+)+-\d$/;
// Cluster identifiers and Serverless workgroup names: lowercase alnum + hyphens.
const IDENTIFIER_RE = /^[a-z0-9][a-z0-9-]*$/;
// Redshift database and user names (the conservative subset we interpolate).
const DB_NAME_RE = /^[a-zA-Z_][a-zA-Z0-9_]*$/;
const IPV4_RE = /^\d{1,3}(\.\d{1,3}){3}$/;

export const AWS_CLI_INSTALL_COMMANDS = {
  mac: 'brew install awscli',
  linux: 'curl "https://awscli.amazonaws.com/awscli-exe-linux-x86_64.zip" -o awscliv2.zip && unzip awscliv2.zip && sudo ./aws/install',
  windows: 'winget install Amazon.AWSCLI',
};

export const awsInstallCommandFor = (os) => {
  if (os === 'windows') return AWS_CLI_INSTALL_COMMANDS.windows;
  return /mac/i.test(navigator.userAgent || '')
    ? AWS_CLI_INSTALL_COMMANDS.mac
    : AWS_CLI_INSTALL_COMMANDS.linux;
};

const DB_USER = 'qombra_connector';
const CREDS_FILENAME = 'qombra-redshift.json';

const normalize = (params) => ({
  deployment: params.deployment === 'serverless' ? 'serverless' : 'provisioned',
  region: String(params.region || '').trim(),
  identifier: String(params.identifier || '').trim(),
  database: String(params.database || '').trim(),
  adminUser: String(params.adminUser || 'awsuser').trim(),
});

export const isValidRedshiftSetup = (params) => {
  const p = normalize(params);
  return (
    REGION_RE.test(p.region) &&
    IDENTIFIER_RE.test(p.identifier) &&
    DB_NAME_RE.test(p.database) &&
    (p.deployment === 'serverless' || DB_NAME_RE.test(p.adminUser))
  );
};

const bashScript = (p, qombraIp) => {
  const serverless = p.deployment === 'serverless';
  const idVar = serverless ? 'WORKGROUP' : 'CLUSTER_ID';
  const dataApiTarget = serverless
    ? '--workgroup-name "$WORKGROUP"'
    : '--cluster-identifier "$CLUSTER_ID" --db-user "$ADMIN_DB_USER"';
  const describe = serverless
    ? `aws redshift-serverless get-workgroup --region "$REGION" --workgroup-name "$WORKGROUP" \\
  --query 'workgroup.[endpoint.address,endpoint.port,publiclyAccessible,securityGroupIds[0]]' --output text`
    : `aws redshift describe-clusters --region "$REGION" --cluster-identifier "$CLUSTER_ID" \\
  --query 'Clusters[0].[Endpoint.Address,Endpoint.Port,PubliclyAccessible,VpcSecurityGroups[0].VpcSecurityGroupId]' --output text`;
  // No shebang, and the body runs via \`bash <<'...'\`: this is pasted into an
  // interactive terminal, where a shebang trips zsh history expansion
  // ("event not found") and where set -e / functions would otherwise leak
  // into (or terminate) the user's own shell.
  return `bash <<'QOMBRA_SETUP'
set -euo pipefail
REGION="${p.region}"
${idVar}="${p.identifier}"
DB="${p.database}"
${serverless ? '' : `ADMIN_DB_USER="${p.adminUser}"  # existing admin the SQL runs as (via temporary credentials)\n`}QOMBRA_IP="${qombraIp || ''}"
DB_USER="${DB_USER}"
DEST="$HOME/Desktop"; [ -d "$DEST" ] || DEST="$HOME"
OUT="$DEST/${CREDS_FILENAME}"

# 1. Resolve the endpoint and networking facts
read -r RS_HOST RS_PORT RS_PUBLIC SG_ID <<< "$(${describe})"
[ -n "$RS_HOST" ] && [ "$RS_HOST" != "None" ] || { echo "ERROR: could not resolve the endpoint - check region and ${serverless ? 'workgroup name' : 'cluster identifier'}."; exit 1; }
echo "Endpoint: $RS_HOST:$RS_PORT"

# 2. Read-only database user with a locally generated password
#    (21 random chars + a suffix that satisfies Redshift's character rules)
PASSWORD="$(LC_ALL=C tr -dc 'A-Za-z0-9' < /dev/urandom | head -c 21 || true)Aa1"
[ \${#PASSWORD} -ge 12 ] || { echo "ERROR: could not generate a password."; exit 1; }

run_sql() {  # execute one statement via the Redshift Data API and wait for it
  local STATUS  # SID stays global so callers can fetch the statement's result
  SID=$(aws redshift-data execute-statement --region "$REGION" ${dataApiTarget} \\
    --database "$DB" --sql "$1" --query Id --output text)
  for i in $(seq 1 60); do
    STATUS=$(aws redshift-data describe-statement --region "$REGION" --id "$SID" --query Status --output text)
    case "$STATUS" in
      FINISHED) return 0 ;;
      FAILED|ABORTED)
        aws redshift-data describe-statement --region "$REGION" --id "$SID" --query Error --output text >&2
        return 1 ;;
    esac
    sleep 2
  done
  echo "Timed out waiting for: $1" >&2; return 1
}

# CREATE for a fresh user, ALTER if a previous run already created it
run_sql "CREATE USER $DB_USER PASSWORD '$PASSWORD'" >/dev/null 2>&1 \\
  || run_sql "ALTER USER $DB_USER PASSWORD '$PASSWORD'"

# Read-only on every non-system schema. Redshift has no GRANT ON ALL
# SCHEMAS, so enumerate the current ones and grant on each. A schema that
# refuses grants (e.g. an undocumented system schema) is skipped, not fatal.
run_sql "SELECT nspname FROM pg_catalog.pg_namespace WHERE nspname NOT LIKE 'pg\\_%' AND nspname NOT IN ('information_schema', 'catalog_history') ORDER BY nspname"
SCHEMAS=$(aws redshift-data get-statement-result --region "$REGION" --id "$SID" \\
  --query 'Records[*][0].stringValue' --output text)
[ -n "$SCHEMAS" ] || { echo "ERROR: could not list schemas."; exit 1; }
GRANTED=""
for S in $SCHEMAS; do
  if run_sql "GRANT USAGE ON SCHEMA \\"$S\\" TO $DB_USER" \\
    && run_sql "GRANT SELECT ON ALL TABLES IN SCHEMA \\"$S\\" TO $DB_USER" \\
    && run_sql "ALTER DEFAULT PRIVILEGES IN SCHEMA \\"$S\\" GRANT SELECT ON TABLES TO $DB_USER"; then
    GRANTED="$GRANTED $S"
    # Write-back: Qombra creates its own qombra_-prefixed prediction tables
    # and writes only to those. Best-effort: a refusal costs writes, not reads.
    run_sql "GRANT CREATE ON SCHEMA \\"$S\\" TO $DB_USER" \\
      || echo "WARNING: could not grant CREATE on $S - Qombra cannot write prediction tables there."
  else
    echo "WARNING: skipped schema $S (see error above)."
  fi
done
[ -n "$GRANTED" ] || { echo "ERROR: no schema could be granted."; exit 1; }
echo "Database user $DB_USER is ready (read access on:$GRANTED)."
echo "Note: schemas created in the future need a re-run of these grants."

# 3. Allow Qombra's IP through the security group (skipped if already present)
if [ -n "$QOMBRA_IP" ] && [ "$SG_ID" != "None" ]; then
  aws ec2 authorize-security-group-ingress --region "$REGION" --group-id "$SG_ID" \\
    --protocol tcp --port "$RS_PORT" --cidr "$QOMBRA_IP/32" >/dev/null 2>&1 \\
    && echo "Security group $SG_ID now allows $QOMBRA_IP on port $RS_PORT." \\
    || echo "Security group rule not added (it may already exist)."
else
  echo "NOTE: add an inbound rule for Qombra's IP on port $RS_PORT to security group $SG_ID yourself."
fi
if [ "$RS_PUBLIC" != "True" ] && [ "$RS_PUBLIC" != "true" ]; then
  echo ""
  echo "WARNING: this endpoint is NOT publicly accessible, so Qombra cannot reach it yet."
  echo "Enable 'Publicly accessible' on the ${serverless ? 'workgroup' : 'cluster'}, or use PrivateLink/VPN per your network policy."
fi

# 4. Credentials file for upload into Qombra
cat > "$OUT" <<EOF
{
  "connector": "redshift",
  "host": "$RS_HOST",
  "port": $RS_PORT,
  "database": "$DB",
  "user": "$DB_USER",
  "password": "$PASSWORD"
}
EOF
chmod 600 "$OUT"
echo ""
echo "Done - credentials saved to: $OUT"
echo "Upload this file in Qombra to finish connecting."
QOMBRA_SETUP
`;
};

const powershellScript = (p, qombraIp) => {
  const serverless = p.deployment === 'serverless';
  const idVar = serverless ? '$WORKGROUP' : '$CLUSTER_ID';
  const dataApiTarget = serverless
    ? '--workgroup-name $WORKGROUP'
    : '--cluster-identifier $CLUSTER_ID --db-user $ADMIN_DB_USER';
  const describe = serverless
    ? `aws redshift-serverless get-workgroup --region $REGION --workgroup-name $WORKGROUP \`
  --query "workgroup.[endpoint.address,endpoint.port,publiclyAccessible,securityGroupIds[0]]" --output text`
    : `aws redshift describe-clusters --region $REGION --cluster-identifier $CLUSTER_ID \`
  --query "Clusters[0].[Endpoint.Address,Endpoint.Port,PubliclyAccessible,VpcSecurityGroups[0].VpcSecurityGroupId]" --output text`;
  return `$ErrorActionPreference = "Continue"
$REGION = "${p.region}"
${idVar} = "${p.identifier}"
$DB = "${p.database}"
${serverless ? '' : `$ADMIN_DB_USER = "${p.adminUser}"  # existing admin the SQL runs as (via temporary credentials)\n`}$QOMBRA_IP = "${qombraIp || ''}"
$DB_USER = "${DB_USER}"
$DEST = Join-Path $HOME "Desktop"; if (-not (Test-Path $DEST)) { $DEST = $HOME }
$OUT = Join-Path $DEST "${CREDS_FILENAME}"

# 1. Resolve the endpoint and networking facts
$RS_HOST, $RS_PORT, $RS_PUBLIC, $SG_ID = (${describe}) -split "\\s+"
if (-not $RS_HOST) { Write-Host "ERROR: could not resolve the endpoint."; exit 1 }
Write-Host "Endpoint: $RS_HOST\`:$RS_PORT"

# 2. Read-only database user with a locally generated password
#    (21 random chars + a suffix that satisfies Redshift's character rules)
$bytes = New-Object byte[] 21
[Security.Cryptography.RandomNumberGenerator]::Create().GetBytes($bytes)
$chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789"
$PASSWORD = (-join ($bytes | ForEach-Object { $chars[$_ % $chars.Length] })) + "Aa1"

function Invoke-Sql($sql) {  # execute one statement via the Redshift Data API and wait for it
  $global:SID = aws redshift-data execute-statement --region $REGION ${dataApiTarget} \`
    --database $DB --sql $sql --query Id --output text
  for ($i = 0; $i -lt 60; $i++) {
    $s = aws redshift-data describe-statement --region $REGION --id $global:SID --query Status --output text
    if ($s -eq "FINISHED") { return $true }
    if ($s -eq "FAILED" -or $s -eq "ABORTED") {
      aws redshift-data describe-statement --region $REGION --id $global:SID --query Error --output text | Write-Host
      return $false
    }
    Start-Sleep 2
  }
  Write-Host "Timed out waiting for: $sql"; return $false
}

# CREATE for a fresh user, ALTER if a previous run already created it
if (-not (Invoke-Sql "CREATE USER $DB_USER PASSWORD '$PASSWORD'")) {
  if (-not (Invoke-Sql "ALTER USER $DB_USER PASSWORD '$PASSWORD'")) { Write-Host "ERROR: could not create the user."; exit 1 }
}

# Read-only on every non-system schema. Redshift has no GRANT ON ALL
# SCHEMAS, so enumerate the current ones and grant on each. A schema that
# refuses grants (e.g. an undocumented system schema) is skipped, not fatal.
if (-not (Invoke-Sql "SELECT nspname FROM pg_catalog.pg_namespace WHERE nspname NOT LIKE 'pg\\_%' AND nspname NOT IN ('information_schema', 'catalog_history') ORDER BY nspname")) { exit 1 }
$SCHEMAS = (aws redshift-data get-statement-result --region $REGION --id $global:SID \`
  --query "Records[*][0].stringValue" --output text) -split "\\s+" | Where-Object { $_ }
if (-not $SCHEMAS) { Write-Host "ERROR: could not list schemas."; exit 1 }
$GRANTED = @()
foreach ($S in $SCHEMAS) {
  $ok = (Invoke-Sql "GRANT USAGE ON SCHEMA ""$S"" TO $DB_USER") \`
    -and (Invoke-Sql "GRANT SELECT ON ALL TABLES IN SCHEMA ""$S"" TO $DB_USER") \`
    -and (Invoke-Sql "ALTER DEFAULT PRIVILEGES IN SCHEMA ""$S"" GRANT SELECT ON TABLES TO $DB_USER")
  if ($ok) {
    $GRANTED += $S
    # Write-back: Qombra creates its own qombra_-prefixed prediction tables
    # and writes only to those. Best-effort: a refusal costs writes, not reads.
    if (-not (Invoke-Sql "GRANT CREATE ON SCHEMA ""$S"" TO $DB_USER")) {
      Write-Host "WARNING: could not grant CREATE on $S - Qombra cannot write prediction tables there."
    }
  } else { Write-Host "WARNING: skipped schema $S (see error above)." }
}
if (-not $GRANTED) { Write-Host "ERROR: no schema could be granted."; exit 1 }
Write-Host "Database user $DB_USER is ready (read access on: $GRANTED)."
Write-Host "Note: schemas created in the future need a re-run of these grants."

# 3. Allow Qombra's IP through the security group (skipped if already present)
if ($QOMBRA_IP -and $SG_ID -ne "None") {
  aws ec2 authorize-security-group-ingress --region $REGION --group-id $SG_ID \`
    --protocol tcp --port $RS_PORT --cidr "$QOMBRA_IP/32" *> $null
  if ($LASTEXITCODE -eq 0) { Write-Host "Security group $SG_ID now allows $QOMBRA_IP on port $RS_PORT." }
  else { Write-Host "Security group rule not added (it may already exist)." }
} else {
  Write-Host "NOTE: add an inbound rule for Qombra's IP on port $RS_PORT to security group $SG_ID yourself."
}
if ($RS_PUBLIC -ne "True") {
  Write-Host ""
  Write-Host "WARNING: this endpoint is NOT publicly accessible, so Qombra cannot reach it yet."
  Write-Host "Enable 'Publicly accessible' on the ${serverless ? 'workgroup' : 'cluster'}, or use PrivateLink/VPN per your network policy."
}

# 4. Credentials file for upload into Qombra
[ordered]@{
  connector = "redshift"; host = $RS_HOST; port = [int]$RS_PORT
  database = $DB; user = $DB_USER; password = $PASSWORD
} | ConvertTo-Json | Set-Content -Path $OUT -Encoding UTF8
Write-Host ""
Write-Host "Done - credentials saved to: $OUT"
Write-Host "Upload this file in Qombra to finish connecting."
`;
};

export const buildRedshiftGuidedScript = (params, os, qombraIp) => {
  if (!isValidRedshiftSetup(params)) return null;
  const ip = IPV4_RE.test(String(qombraIp || '').trim()) ? String(qombraIp).trim() : '';
  const p = normalize(params);
  return os === 'windows' ? powershellScript(p, ip) : bashScript(p, ip);
};

export { CREDS_FILENAME as REDSHIFT_CREDS_FILENAME, DB_USER as REDSHIFT_DB_USER };
